import { useState } from 'react'
import { Card, Button, Table, Badge, Modal, Form, Row, Col, Spinner, Alert } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import api from '../../services/api'
import { CertIndicadorDot } from '../common/CertIndicadorDot'
import { CERT_INDICADOR_LABELS } from '../../constants/certIndicador'

// Helper para obtener nombre del cliente
const getClientName = (client) => {
  if (client?.razon_social) return client.razon_social
  if (client?.business_name) return client.business_name
  const parts = []
  if (client?.nombre) parts.push(client.nombre)
  if (client?.apellido_paterno) parts.push(client.apellido_paterno)
  if (parts.length > 0) return parts.join(' ')
  return client?.name || 'N/A'
}

// Opciones de tipos de PAC (solo Multifacturas visible)
const TIPO_PAC_OPTIONS = [
  { value: 'multifactura', label: 'MultiFactura' },
]

const PACConfigurations = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  // Igual que el backend (`views_pac._es_superadmin`): el rol cuenta, no sólo `is_superuser`.
  const isSuperAdmin = !!(user?.is_superuser || user?.role === 'superadmin')
  const [showModal, setShowModal] = useState(false)
  const [editingConfig, setEditingConfig] = useState(null)
  const [validatingId, setValidatingId] = useState(null)
  
  // Filtros
  const { clientId: filterCliente, setClientId: setFilterCliente, selectDisabled } = useSyncedLocalClientId()
  const [filterTipoPAC, setFilterTipoPAC] = useState('')
  const [filterAmbiente, setFilterAmbiente] = useState('')
  const [filterActivo, setFilterActivo] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  
  // Formulario
  const [formData, setFormData] = useState({
    cliente: '',
    nombre: '',
    tipo_pac: 'multifactura',
    ambiente: 'pruebas',
    configuracion_global: '',
    usuario: '',
    password: '',
    url_timbrado: '',
    url_cancelacion: '',
    url_estatus: '',
    certificado_csd: '',
    llave_privada_csd: '',
    password_csd: '',
    activo: true,
    es_predeterminado: false,
    notas: '',
  })
  
  // Archivos seleccionados para certificados
  const [certificadoFile, setCertificadoFile] = useState(null)
  const [llaveFile, setLlaveFile] = useState(null)

  // Obtener clientes
  const { data: clients } = useQuery(
    'clients-for-pac',
    async () => {
      const response = await api.get('/clients/clients/')
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )
  
  // Obtener configuraciones globales activas (todos los usuarios necesitan verlas para seleccionarlas)
  const { data: configsGlobalesData, isLoading: loadingGlobales } = useQuery(
    'pac-global-configs',
    async () => {
      try {
        const response = await api.get('/financial/configuraciones-pac-global/', { 
          params: { activo: true } 
        })
        return response.data.results || response.data || []
      } catch (error) {
        // Si no tiene permisos, retornar array vacío
        if (error.response?.status === 403 || error.response?.status === 404) {
          return []
        }
        throw error
      }
    },
    {
      // Sólo el superadmin las ve (son las credenciales maestras); a un contador el servidor le
      // asigna la cuenta, así que pedirlas sólo produciría un 403.
      enabled: !!user && isSuperAdmin,
      retry: false,
      staleTime: 5 * 60 * 1000 // Cache por 5 minutos
    }
  )
  
  const configsGlobales = configsGlobalesData || []

  // Obtener configuraciones PAC
  const { data: configsData, isLoading } = useQuery(
    ['pac-configs', filterCliente, filterTipoPAC, filterAmbiente, filterActivo, searchTerm],
    async () => {
      const params = {}
      if (filterCliente) params.cliente_id = filterCliente
      if (filterTipoPAC) params.tipo_pac = filterTipoPAC
      if (filterAmbiente) params.ambiente = filterAmbiente
      if (filterActivo !== '') params.activo = filterActivo === 'true'
      if (searchTerm) params.search = searchTerm
      
      const response = await api.get('/financial/configuraciones-pac/', { params })
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  const configs = configsData || []

  // Mutación para crear/actualizar
  const saveMutation = useMutation(
    async (data) => {
      if (editingConfig) {
        return api.put(`/financial/configuraciones-pac/${editingConfig.id}/`, data)
      } else {
        return api.post('/financial/configuraciones-pac/', data)
      }
    },
    {
      onSuccess: () => {
        toast.success(editingConfig ? 'Configuración actualizada correctamente' : 'Configuración creada correctamente')
        queryClient.invalidateQueries('pac-configs')
        handleCloseModal()
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || 
                        error.response?.data?.detail ||
                        (typeof error.response?.data === 'object' ? JSON.stringify(error.response.data) : 'Error al guardar la configuración')
        toast.error(errorMsg)
      }
    }
  )

  // Mutación para eliminar
  const deleteMutation = useMutation(
    async (id) => {
      await api.delete(`/financial/configuraciones-pac/${id}/`)
    },
    {
      onSuccess: () => {
        toast.success('Configuración eliminada correctamente')
        queryClient.invalidateQueries('pac-configs')
      },
      onError: () => {
        toast.error('Error al eliminar la configuración')
      }
    }
  )

  // Mutación para validar credenciales
  const validateMutation = useMutation(
    async (id) => {
      const response = await api.post(`/financial/configuraciones-pac/${id}/validar_servicio/`)
      return response.data
    },
    {
      onSuccess: (data) => {
        if (data.valid) {
          toast.success(data.message || 'Certificados válidos: .cer y .key corresponden al cliente y la contraseña es correcta')
        } else {
          toast.warning(data.message || 'Los certificados no son válidos')
        }
        setValidatingId(null)
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Error al validar certificados'
        toast.error(errorMsg)
        setValidatingId(null)
      }
    }
  )

  const handleOpenModal = (config = null) => {
    if (config) {
      setEditingConfig(config)
      // Obtener ID de configuracion_global (puede venir como objeto o como ID)
      const configGlobalId = config.configuracion_global?.id || config.configuracion_global || ''
      
      setFormData({
        cliente: config.cliente?.id || config.cliente || '',
        nombre: config.nombre || '',
        tipo_pac: config.tipo_pac || 'multifactura',
        ambiente: config.ambiente || 'pruebas',
        configuracion_global: configGlobalId ? String(configGlobalId) : '',
        usuario: '',
        password: '',
        url_timbrado: '',
        url_cancelacion: '',
        url_estatus: '',
        certificado_csd: '',
        llave_privada_csd: '',
        password_csd: '',
        activo: config.activo !== undefined ? config.activo : true,
        es_predeterminado: config.es_predeterminado || false,
        notas: config.notas || '',
      })
    } else {
      setEditingConfig(null)
      setFormData({
        cliente: '',
        nombre: '',
        tipo_pac: 'multifactura',
        ambiente: 'pruebas',
        configuracion_global: '',
        usuario: '',
        password: '',
        url_timbrado: '',
        url_cancelacion: '',
        url_estatus: '',
        certificado_csd: '',
        llave_privada_csd: '',
        password_csd: '',
        activo: true,
        es_predeterminado: false,
        notas: '',
      })
    }
    setCertificadoFile(null)
    setLlaveFile(null)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingConfig(null)
    setCertificadoFile(null)
    setLlaveFile(null)
    setFormData({
      cliente: '',
      nombre: '',
      tipo_pac: 'multifactura',
      ambiente: 'pruebas',
      configuracion_global: '',
      usuario: '',
      password: '',
      url_timbrado: '',
      url_cancelacion: '',
      url_estatus: '',
      certificado_csd: '',
      llave_privada_csd: '',
      password_csd: '',
      activo: true,
      es_predeterminado: false,
      notas: '',
    })
  }
  
  const handleFileRead = (file, field) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const content = e.target.result
        // Extraer solo la parte base64 (sin prefijo data:...)
        const base64Content = content.includes(',') ? content.split(',')[1] : content
        setFormData(prev => ({ ...prev, [field]: base64Content }))
        resolve(base64Content)
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }
  
  const handleCertificadoChange = async (e) => {
    const file = e.target.files[0]
    if (file) {
      if (!file.name.toLowerCase().endsWith('.cer')) {
        toast.error('El archivo debe ser un certificado .cer')
        return
      }
      setCertificadoFile(file)
      await handleFileRead(file, 'certificado_csd')
    }
  }
  
  const handleLlaveChange = async (e) => {
    const file = e.target.files[0]
    if (file) {
      if (!file.name.toLowerCase().endsWith('.key')) {
        toast.error('El archivo debe ser una llave privada .key')
        return
      }
      setLlaveFile(file)
      await handleFileRead(file, 'llave_privada_csd')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // El superadmin elige la cuenta maestra; a un contador se la asigna el servidor.
    if (isSuperAdmin && !formData.configuracion_global) {
      toast.error('Debe seleccionar una configuración global')
      return
    }
    
    // Preparar datos para enviar
    // tipo_pac siempre será 'multifactura' (oculto para el usuario)
    const dataToSend = {
      cliente: parseInt(formData.cliente),
      nombre: formData.nombre,
      tipo_pac: 'multifactura', // Siempre multifactura, campo oculto
      ambiente: formData.ambiente,
      activo: formData.activo,
      es_predeterminado: formData.es_predeterminado,
      notas: formData.notas || '',
    }
    
    // Un contador no manda cuenta, ambiente ni credenciales: el servidor los ignora y asigna la
    // cuenta maestra (views_pac.ConfiguracionPACViewSet).
    if (!isSuperAdmin) {
      delete dataToSend.ambiente
    } else if (formData.configuracion_global) {
      // Si tiene configuración global, incluirla y NO enviar URLs ni credenciales (vienen de la global)
      dataToSend.configuracion_global = parseInt(formData.configuracion_global)
      // NO incluir usuario, password ni URLs cuando hay configuración global
      // Estos campos se obtienen automáticamente de la configuración global
    } else {
      // Solo incluir campos locales si NO hay configuración global
      if (formData.usuario) {
        dataToSend.usuario = formData.usuario
      }
      if (formData.url_timbrado) {
        dataToSend.url_timbrado = formData.url_timbrado
      }
      if (formData.url_cancelacion) {
        dataToSend.url_cancelacion = formData.url_cancelacion || null
      }
      if (formData.url_estatus) {
        dataToSend.url_estatus = formData.url_estatus || null
      }
    }

    // Solo incluir credenciales si se están cambiando
    if (formData.password) {
      dataToSend.password = formData.password
    }
    
    // Certificados (siempre requeridos para Multifacturas)
    if (formData.certificado_csd) {
      dataToSend.certificado_csd = formData.certificado_csd
    }
    if (formData.llave_privada_csd) {
      dataToSend.llave_privada_csd = formData.llave_privada_csd
    }
    if (formData.password_csd) {
      dataToSend.password_csd = formData.password_csd
    }

    saveMutation.mutate(dataToSend)
  }

  const handleDelete = (id) => {
    if (window.confirm('¿Está seguro de que desea eliminar esta configuración?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleValidate = (id) => {
    setValidatingId(id)
    validateMutation.mutate(id)
  }

  return (
    <div>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            {isSuperAdmin ? 'Configuraciones de PAC' : 'Certificados de sello digital (CSD)'}
          </h5>
          <Button variant="primary" onClick={() => handleOpenModal()}>
            <i className="bi bi-plus-circle me-1"></i>
            {isSuperAdmin ? 'Nueva Configuración' : 'Cargar CSD'}
          </Button>
        </Card.Header>
        <Card.Body>
          {/* Filtros */}
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Cliente</Form.Label>
                <Form.Select
                  value={filterCliente}
                  onChange={(e) => setFilterCliente(e.target.value)}
                  disabled={selectDisabled}
                >
                  <option value="">Todos</option>
                  {clients?.map(client => (
                    <option key={client.id} value={client.id}>
                      {getClientName(client)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Tipo PAC</Form.Label>
                <Form.Select
                  value={filterTipoPAC}
                  onChange={(e) => setFilterTipoPAC(e.target.value)}
                >
                  <option value="">Todos</option>
                  {TIPO_PAC_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Ambiente</Form.Label>
                <Form.Select
                  value={filterAmbiente}
                  onChange={(e) => setFilterAmbiente(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="pruebas">Pruebas</option>
                  <option value="produccion">Producción</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Estado</Form.Label>
                <Form.Select
                  value={filterActivo}
                  onChange={(e) => setFilterActivo(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="true">Activo</option>
                  <option value="false">Inactivo</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Buscar</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Nombre, cliente, usuario..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Tabla */}
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Nombre</th>
                  <th title="Vigencia CSD">CSD</th>
                  {isSuperAdmin && <th>Tipo PAC</th>}
                  <th>Ambiente</th>
                  <th>Usuario</th>
                  <th>Estado</th>
                  <th>Predeterminado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {configs.length === 0 ? (
                  <tr>
                    <td colSpan={isSuperAdmin ? 9 : 8} className="text-center">
                      No se encontraron configuraciones
                    </td>
                  </tr>
                ) : (
                  configs.map(config => (
                    <tr key={config.id}>
                      <td>{config.cliente_name || getClientName(config.cliente)}</td>
                      <td>{config.nombre}</td>
                      <td className="text-center">
                        <CertIndicadorDot
                          indicador={config.csd_indicador}
                          title={`CSD: ${CERT_INDICADOR_LABELS[config.csd_indicador] || CERT_INDICADOR_LABELS.none}${config.vigente_hasta ? ` · hasta ${config.vigente_hasta}` : ''}`}
                        />
                      </td>
                      {isSuperAdmin && (
                        <td>
                          {TIPO_PAC_OPTIONS.find(opt => opt.value === config.tipo_pac)?.label || config.tipo_pac}
                        </td>
                      )}
                      <td>
                        <Badge bg={config.ambiente === 'produccion' ? 'danger' : 'warning'}>
                          {config.ambiente === 'produccion' ? 'Producción' : 'Pruebas'}
                        </Badge>
                      </td>
                      <td>{config.usuario}</td>
                      <td>
                        <Badge bg={config.activo ? 'success' : 'secondary'}>
                          {config.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="text-center">
                        {config.es_predeterminado && <Badge bg="primary">Sí</Badge>}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => handleValidate(config.id)}
                            disabled={validatingId === config.id}
                            title="Validar que .cer y .key correspondan al cliente y la contraseña sea correcta"
                            style={{ minWidth: '100px' }}
                          >
                            {validatingId === config.id ? (
                              <>
                                <Spinner size="sm" className="me-1" />
                                <span>Validando...</span>
                              </>
                            ) : (
                              <>
                                <i className="bi bi-check-circle me-1"></i>
                                <span>Validar</span>
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleOpenModal(config)}
                            title="Editar"
                            style={{ minWidth: '80px' }}
                          >
                            <i className="bi bi-pencil me-1"></i>
                            <span>Editar</span>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(config.id)}
                            title="Eliminar"
                            style={{ minWidth: '80px' }}
                          >
                            <i className="bi bi-trash me-1"></i>
                            <span>Eliminar</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal para crear/editar */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {isSuperAdmin
              ? (editingConfig ? 'Editar Configuración PAC' : 'Nueva Configuración PAC')
              : (editingConfig ? 'Editar CSD del contribuyente' : 'Cargar CSD del contribuyente')}
          </Modal.Title>
        </Modal.Header>
        <Form
          onSubmit={handleSubmit}
          autoComplete="off"
          name="pac-config-form"
        >
          <Modal.Body>
            {/*
              Evita que el gestor de contraseñas del navegador rellene "usuario" y "contraseña"
              en campos que no son del login de la app.
            */}
            <input
              type="text"
              name="pac-honeypot-user"
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              className="position-absolute"
              style={{ opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
              readOnly
            />
            <input
              type="password"
              name="pac-honeypot-pass"
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              className="position-absolute"
              style={{ opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
              readOnly
            />
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cliente *</Form.Label>
                  <Form.Select
                    value={formData.cliente}
                    onChange={(e) => setFormData({ ...formData, cliente: e.target.value })}
                    required
                    disabled={!!editingConfig}
                    autoComplete="off"
                  >
                    <option value="">Seleccione un cliente</option>
                    {clients?.map(client => (
                      <option key={client.id} value={client.id}>
                        {getClientName(client)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre de Configuración *</Form.Label>
                  <Form.Control
                    type="text"
                    name="pac_nombre_configuracion"
                    id="pac-nombre-configuracion"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    required
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-form-type="other"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>{isSuperAdmin ? 'Configuración Global *' : 'Cuenta de timbrado'}</Form.Label>
                  {!isSuperAdmin ? (
                    <Alert variant="info" className="mb-0" data-testid="pac-cuenta-asignada">
                      La cuenta de timbrado la asigna el sistema. Sólo necesitas cargar el
                      certificado de sello digital (CSD) de tu contribuyente y su contraseña.
                    </Alert>
                  ) : loadingGlobales ? (
                    <div className="d-flex align-items-center">
                      <Spinner size="sm" className="me-2" />
                      <span>Cargando configuraciones globales...</span>
                    </div>
                  ) : configsGlobales.length === 0 ? (
                    <Alert variant="warning" className="mb-0">
                      <strong>No hay configuraciones globales disponibles.</strong>
                      <br />
                      {user?.is_superuser ? (
                        <>Por favor, cree una configuración global primero en la pestaña &quot;Configuración PAC Global (Admin)&quot;.</>
                      ) : (
                        <>Contacte al administrador para crear una configuración global.</>
                      )}
                    </Alert>
                  ) : (
                    <>
                      <Form.Select
                        value={formData.configuracion_global}
                        onChange={(e) => {
                          const selected = configsGlobales.find(c => c.id === parseInt(e.target.value))
                          setFormData(prev => ({
                            ...prev,
                            configuracion_global: e.target.value,
                            ambiente: selected?.ambiente || prev.ambiente,
                            usuario: '',
                            password: '',
                            url_timbrado: '',
                            url_cancelacion: '',
                            url_estatus: '',
                            password_csd: prev.password_csd
                          }))
                        }}
                        required
                      >
                        <option value="">Seleccione una configuración global</option>
                        {configsGlobales.map(config => (
                          <option key={config.id} value={config.id}>
                            {config.nombre} ({config.ambiente_display || (config.ambiente === 'produccion' ? 'Producción' : 'Pruebas')})
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">Seleccione una configuración global (prod o demo). Las URLs se obtendrán automáticamente.</Form.Text>
                    </>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {formData.configuracion_global && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Ambiente</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.ambiente === 'produccion' ? 'Producción' : 'Pruebas'}
                      disabled
                      readOnly
                    />
                    <Form.Text className="text-muted">Se obtiene automáticamente de la configuración global</Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}

            {formData.configuracion_global && (
              <Alert variant="info" className="mb-3">
                <strong>Configuración Global:</strong> Los datos de usuario, contraseña y URLs se obtendrán automáticamente de la configuración global seleccionada.
                Solo necesita proporcionar los certificados CSD del cliente.
              </Alert>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Certificado CSD (.cer) *</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".cer"
                    onChange={handleCertificadoChange}
                    required={!editingConfig}
                  />
                  {certificadoFile && (
                    <Form.Text className="text-success">
                      Archivo seleccionado: {certificadoFile.name}
                    </Form.Text>
                  )}
                  <Form.Text className="text-muted">
                    Archivo .cer del certificado de sello digital
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Llave Privada CSD (.key) *</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".key"
                    onChange={handleLlaveChange}
                    required={!editingConfig}
                  />
                  {llaveFile && (
                    <Form.Text className="text-success">
                      Archivo seleccionado: {llaveFile.name}
                    </Form.Text>
                  )}
                  <Form.Text className="text-muted">
                    Archivo .key de la llave privada del certificado
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Contraseña CSD {editingConfig && '(dejar vacío para no cambiar)'}</Form.Label>
                  <Form.Control
                    type="password"
                    name="pac_password_csd_certificado"
                    id="pac-password-csd"
                    value={formData.password_csd}
                    onChange={(e) => setFormData({ ...formData, password_csd: e.target.value })}
                    autoComplete="new-password"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-form-type="other"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Estado</Form.Label>
                  <Form.Check
                    type="switch"
                    label="Activo"
                    checked={formData.activo}
                    onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    label="Predeterminado"
                    checked={formData.es_predeterminado}
                    onChange={(e) => setFormData({ ...formData, es_predeterminado: e.target.checked })}
                  />
                  <Form.Text className="text-muted">
                    Esta configuración se usará por defecto para el cliente
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Notas</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.notas}
                    onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={saveMutation.isLoading}>
              {saveMutation.isLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Guardando...
                </>
              ) : (
                editingConfig ? 'Actualizar' : 'Crear'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}

export default PACConfigurations
